import { AppDispatch, RootState } from '../reducers/store';
import { useDispatch, useSelector } from 'react-redux';

import Typography from '@mui/material/Typography';
import Grid2 from '@mui/material/Grid2';

import { useEffect, useState } from 'react';
import { Program, ProgramUser } from '../model/program';

import { putProgram } from '../api/programs';

import { getApiAccessToken } from '../auth/auth';
import { AxiosError } from 'axios';
import { AlertView } from './alert';
import { useMsal } from '@azure/msal-react';
import { DeleteDialog } from './deleteconfirm';
import { cloneProgramAsync, deleteProgramAsync, updateProgram } from '../reducers/programSlice';
import { getPermissionUsersAsync, getProgramUsersAsync, getRoleUsersAsync } from '../reducers/userSlice';
import { DocumentsUploadView } from './documentupload';
import { UserCertificationView } from './usercertifications';

import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import TextField from '@mui/material/TextField';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import EditIcon from '@mui/icons-material/Edit';
import LoginIcon from '@mui/icons-material/Login';

import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import VerifiedIcon from '@mui/icons-material/Verified';
import PeopleIcon from '@mui/icons-material/People';
import DescriptionIcon from '@mui/icons-material/Description';
import DashboardIcon from '@mui/icons-material/Dashboard';
import TimelineIcon from '@mui/icons-material/Timeline';

import { UserView } from './users';
import { Overview } from './overview';
import Box from '@mui/material/Box';
import { Badge, Card, CardContent, Grid, Switch, Tooltip } from '@mui/material';
import { Loader } from './loader';
import { User } from '../model/user';
import { UserCertification } from '../model/certification';
import { getUserCertificationsAsync } from '../reducers/certificationSlice';
import { getGroupUsersAsync } from '../reducers/userSlice';
import { Group } from '../model/group';
import { getUserAccess, UserPermission, UserRole } from '../auth/access';
import { UserProgressView } from './userprogress';
import { UserProgress } from '../model/progress';
import { getUsersProgramProgressAsync } from '../reducers/progressSlice';
import { CloneDialog } from './clonedialog';
import { getCoursesAsync } from '../reducers/courseSlice';

interface TabInfo {
    name: string
    index: number
}

interface Summary {
    title: string
    value: React.ReactNode;
    description: string 
    icon: React.ReactNode
}

export const ProgramView = () => {
    const { instance } = useMsal();
    const activeAccount = instance.getActiveAccount();
    const dispatch: AppDispatch = useDispatch();
    const userRole = getUserAccess(activeAccount)?.role;

    const groups: Group[] = useSelector((state: RootState) => state.groups.groups);
    const allUsers: User[] = useSelector((state: RootState) => state.users.users);
    const currentProgram: Program | null = useSelector((state: RootState) => state.programs.currentProgram);
    const currentGroup: Group | null = useSelector((state: RootState) => state.groups.currentGroup);
    const currentGroupUsers: User[] = useSelector((state: RootState) => state.users.currentGroupUsers);
    const currentProgramUsers: ProgramUser[] | null = useSelector((state: RootState) => state.users.currentProgramUsers);
    const currentUserCertifications: UserCertification[] | null = useSelector((state: RootState) => state.certifications.currentUserCertifications);
    const currentUsersProgramProgress: UserProgress[] | null = useSelector((state: RootState) => state.progress.currentUsersProgramProgress);
    
    const [title, setTitle] = useState<string | undefined>(currentProgram?.name);
    const [loading, setLoading] = useState<boolean>(false);
    const [errorMessage, setError] = useState<string | null>(null);
    const [isEditingTitle, setIsEditingTitle] = useState(false);
    
    const fetchProgramUsers = async (program: Program | null) => {
        if (currentGroup == null || program == null) {
          console.log("could not get users for invalid program and/or group")
          return 
        }

        try {
            setLoading(true)

            const accessToken: string = await getApiAccessToken(instance, activeAccount)
            const userRole = getUserAccess(activeAccount)?.role;

            if (userRole == UserRole.ADMIN) {
                dispatch(getRoleUsersAsync({ accessToken, role: UserRole.CLIENT_ADMIN }))
                dispatch(getPermissionUsersAsync({ accessToken, permission: UserPermission.MANAGE_CONTENT }))
            }
            await Promise.all([
                dispatch(getProgramUsersAsync({ accessToken, programId: program.id })),
                dispatch(getUserCertificationsAsync({ accessToken, programId: program.id })),
                dispatch(getUsersProgramProgressAsync({ accessToken, programId: program.id })),
                dispatch(getGroupUsersAsync({ accessToken, groupId: currentGroup.id })),
            ]);

            setError(null)

        } catch (error) {
            if (error instanceof AxiosError) {
                console.log(error)
                setError(error.message)
            } else if (error instanceof Error) {
                console.error(error);
                setError(error.message)
            }
        } finally {
            setLoading(false);
        }
    }

    useEffect(() => {
        fetchProgramUsers(currentProgram)
    }, [currentProgram]);

    const tabs: TabInfo[] = [
        {name: "Overview", index: 0},
        {name: "Users", index: 1},
        {name: "Progress", index: 2},
        {name: "Certifications", index: 3},
        {name: "Documents", index: 4},
    ]

    const [tab, setTab] = useState<TabInfo>(tabs[0]);

    const summaries: Summary[] = [
        { 
            value: (
                loading ? <Loader width={25} height={25}/> :
                currentProgram?.clientName == "Public" ? 
                    <Typography component="span" variant="h5" sx={{ fontSize: '1.2rem', color: 'black' }}>
                        {allUsers?.length ?? 0}
                    </Typography> :
                    <>
                        <Typography component="span" variant="h5" sx={{ fontSize: '1.2rem', color: 'black' }}>
                            {currentProgramUsers?.length ?? 0}
                        </Typography>
                        <Typography component="span" variant="h5" sx={{ fontSize: '0.8rem', color: 'gray', marginLeft: '4px' }}>
                            / {currentGroupUsers.length ?? 0}
                        </Typography>
                    </>
            ), 
            title: "Users", 
            description: currentProgram?.clientName == "Public" ? "All users are part of the public program" : "Users part of this program.", 
            icon: <PeopleIcon /> 
        },
        { 
            value: (
                loading ? <Loader width={25} height={25}/> :
                currentProgram?.clientName == "Public" ? 
                    <>
                        <Typography component="span" variant="h5" sx={{ fontSize: '1.2rem', color: 'black' }}>
                            {allUsers.filter(user => user.verified).length ?? 0}
                        </Typography>
                        <Typography component="span" variant="h5" sx={{ fontSize: '0.8rem', color: 'gray', marginLeft: '4px' }}>
                            / {allUsers.length ?? 0}
                        </Typography>
                    </> :
                    <>
                        <Typography component="span" variant="h5" sx={{ fontSize: '1.2rem', color: 'black' }}>
                            {
                                // programusers (with verified field from 'user') who have been verified to go the mobile or web app (verified field)
                                allUsers.filter(
                                    user => currentProgramUsers?.map(
                                        programUser => programUser.userId
                                    )?.includes(user.id)).filter(user => 
                                        user.verified
                                    ).length ?? 0
                            }
                        </Typography>
                        <Typography component="span" variant="h5" sx={{ fontSize: '0.8rem', color: 'gray', marginLeft: '4px' }}>
                            / {currentGroupUsers.length ?? 0}
                        </Typography>
                    </>
            ), 
            title: "Login",
            description: "Users that have logged into the mobile or web app.", 
            icon: <LoginIcon /> 
        },
        { 
            value: (
                loading ? <Loader width={25} height={25}/> :
                currentProgram?.clientName == "Public" ? 
                    <>
                        <Typography component="span" variant="h5" sx={{ fontSize: '1.2rem', color: 'black' }}>
                            {currentUsersProgramProgress?.length ?? 0}
                        </Typography>
                        <Typography component="span" variant="h5" sx={{ fontSize: '0.8rem', color: 'gray', marginLeft: '4px' }}>
                            / {allUsers.length ?? 0}
                        </Typography>
                    </> :
                    <>
                        <Typography component="span" variant="h5" sx={{ fontSize: '1.2rem', color: 'black' }}>
                            {currentUsersProgramProgress?.length ?? 0}
                        </Typography>
                        <Typography component="span" variant="h5" sx={{ fontSize: '0.8rem', color: 'gray', marginLeft: '4px' }}>
                            / {currentGroupUsers.length ?? 0}
                        </Typography>
                    </>
            ), 
            title: "Progress", 
            description: "Users that have completed at least one chapter.", 
            icon: <TimelineIcon /> 
        },
        { 
            value: (
                loading ? <Loader width={25} height={25}/> :
                currentProgram?.clientName == "Public" ? 
                    <>
                        <Typography component="span" variant="h5" sx={{ fontSize: '1.2rem', color: 'black' }}>
                            {currentUserCertifications?.filter(cert => cert.certified).length ?? 0}
                        </Typography>
                        <Typography component="span" variant="h5" sx={{ fontSize: '0.8rem', color: 'gray', marginLeft: '4px' }}>
                            / {allUsers.length ?? 0}
                        </Typography>
                    </> :
                    <>
                        <Typography component="span" variant="h5" sx={{ fontSize: '1.2rem', color: 'black' }}>
                            {
                                // users with cert data that have been certified for at least one assessment with a threshold
                                currentUserCertifications?.filter(
                                    cert => cert.certified && cert.assessments.some(
                                        assessment => assessment.threshold > 0
                                    )
                                ).length ?? 0
                            }
                        </Typography>
                        <Typography component="span" variant="h5" sx={{ fontSize: '0.8rem', color: 'gray', marginLeft: '4px' }}>
                            / {currentGroupUsers.length ?? 0}
                        </Typography>
                    </>
            ), 
            title: "Certified", 
            description: "Users that passed all the assessments within the program.", 
            icon: <VerifiedIcon />  
        },
    ];

    const handleTabSelect = (event: React.SyntheticEvent<Element, Event>, index: any) => {
        setTab(tabs[index]);
    };

    const handleSaveProgram = async (program: Program | null) => {
        if (program == null) {
          console.log("could not save invalid program")
          return 
        }

        try {
            setLoading(true)

            let accessToken: string = await getApiAccessToken(instance, activeAccount)
            await putProgram(accessToken, program);

            dispatch(updateProgram(program));
            setError(null)

        } catch (error) {
            if (error instanceof AxiosError) {
                console.log(error)
                setError(error.message)
            } else if (error instanceof Error) {
                console.error(error);
                setError(error.message)
            }
        }
  
        setLoading(false)
    }

    const handleCloneProgram = async (clientName: string, program: Program | null) => {
        if (clientName == "" || program == null) {
          console.log("could not clone with invalid parameters")
          return 
        }

        try {
            setLoading(true)
            
            let accessToken: string = await getApiAccessToken(instance, activeAccount)
            dispatch(cloneProgramAsync({accessToken, clientName, program}));

            // used to reload the underlying cloned program content
            dispatch(getCoursesAsync({accessToken}))

        } catch (error) {
            if (error instanceof AxiosError) {
                console.log(error)
                setError(error?.message)
            } else if (error instanceof Error) {
                console.error(error);
            }
        }
  
        setLoading(false)
    }

    const handleDeleteProgram = async (program: Program | null) => {
        if (program == null) {
          console.log("could not delete invalid program")
          return 
        }

        try {
            setLoading(true)
            
            let accessToken: string = await getApiAccessToken(instance, activeAccount)
            dispatch(deleteProgramAsync({accessToken, program}));

        } catch (error) {
            if (error instanceof AxiosError) {
                console.log(error)
                setError(error?.message)
            } else if (error instanceof Error) {
                console.error(error);
            }
        }
  
        setLoading(false)
    }

    const handleTitleEdit = () => {
        setIsEditingTitle(true);
    };

    const handleTitleSave = (title: string | undefined, program: Program | null) => {
        if (title && program) {
            const updatedProgram = { ...program, name: title };
            handleSaveProgram(updatedProgram);
        }
        setIsEditingTitle(false);
    };

    const handleTitleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setTitle(event.target.value);
    };

    const handleTitleCancel = () => {
        setIsEditingTitle(false);
    };

    return (
        <>
            <Grid2 container direction="row">                
                {isEditingTitle ? (
                    <>
                        <TextField
                            value={title || ''}
                            onChange={handleTitleChange}
                            variant="outlined"
                            size="small"
                            sx={{ margin: 2 }}
                            style={{ width: "30%" }}
                        />
                        <Box
                            onClick={() => handleTitleSave(title, currentProgram)} 
                            color="primary" 
                            sx={{
                                display: 'flex',
                                borderRadius: 2,
                                margin: 2,
                                justifyContent: 'center',
                                alignItems: 'center',
                            }}
                            >
                                <CheckIcon />
                        </Box>
                        <Box
                            onClick={handleTitleCancel}
                            color="secondary" 
                            sx={{
                                display: 'flex',
                                borderRadius: 2,
                                margin: 2,
                                justifyContent: 'center',
                                alignItems: 'center',
                            }}
                            >
                                <CloseIcon />
                        </Box>
                    </>
                ) : (
                    <>
                        {loading ? 
                            <Box
                                sx={{
                                    display: 'flex',
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                }}
                            >
                                <Loader width={25} height={25} />
                            </Box> : 
                            <Box
                                sx={{
                                    display: 'flex',
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                }}
                            >
                                <Badge
                                    badgeContent="In Sync"
                                    color={errorMessage ? "error" : "success"}
                                    variant="dot"
                                    sx={{
                                        marginLeft: 2,
                                    }}
                                />
                            </Box>
                        }
                        <Typography
                            variant="h6"
                            color="black"
                            align="left"
                            margin={2}
                            onClick={handleTitleEdit}
                            sx={{ 
                                cursor: 'pointer',
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                            }}
                        >
                            {currentProgram?.name}
                        </Typography>
                        <Box
                            sx={{
                                borderRadius: 2,
                                display: 'inline-block',
                                backgroundColor: '#fafafa',
                                margin: 2,
                            }}
                            onClick={handleTitleEdit} 
                            margin={2} >
                            <EditIcon 
                                sx={{ 
                                    margin: 1,
                                    display: 'flex', 
                                    alignItems: 'center', 
                                    justifyContent: 'center',
                                }}/>
                        </Box>

                        {
                            userRole == UserRole.ADMIN ?
                                <>
                                    <CloneDialog
                                        title={'Clone Program'}
                                        message={'Clone the program to one of the following Clients'}
                                        onCloneConfirm={(clientName) => handleCloneProgram(clientName, currentProgram)} 
                                        clientNames={groups.map(group => group.displayName)}
                                    />

                                    <DeleteDialog 
                                        title={
                                            `Delete ${currentProgram?.name}`
                                        }
                                        message={`Are you sure you want to delete this program ${currentProgram && currentProgram?.courses?.length > 0 ? `, as well as it's ${currentProgram?.courses?.length} Modules` : ''} ?`} 
                                        onDeleteConfirmed={() => {handleDeleteProgram(currentProgram)}}
                                    />
                                </> :
                                <></>
                        }
                    </>
                )}
            </Grid2>

            <AlertView error={errorMessage ? `${errorMessage?.substring(0, 200)}...` : null}/>

            <Grid
                container 
                spacing={3} 
                justifyContent="start"
                sx={{
                    margin: 0,
                }}>
                {summaries.map((card, index) => (
                    <Grid 
                        item
                        md={3} sm={6} xs={12}
                        key={index}
                        >
                        <Card sx={{ height: '100%' }}>
                            <CardContent 
                                sx={{ 
                                    display: 'flex', 
                                    flexDirection: 'row', 
                                    height: '100%' 
                                }}>
                                <Box sx={{ padding: 1.5, display: 'flex'}}>
                                    {card.icon}
                                </Box>
                                <Grid sx={{ 
                                    display: 'flex', 
                                    flexDirection: 'column',
                                    alignItems: 'start',
                                    padding: 1
                                 }} >
                                    <Typography variant="h5" component="div" >
                                        <strong>{card.value}</strong>
                                    </Typography>
                                    <Typography variant="caption" component="div" color='grey'>
                                        {card.title.toUpperCase()}
                                    </Typography>
                                    <Typography variant="caption" color='grey' align="left">
                                        {card.description}
                                    </Typography>
                                </Grid>
                            </CardContent>
                        </Card>
                    </Grid>
                ))}
            </Grid>

            <Tabs
                value={tab.index}
                onChange={handleTabSelect}
                variant="fullWidth" 
                aria-label="tabs to select options within the program"
                sx={{ 
                    backgroundColor: '#f8f8f8ff',
                    borderRadius: '16px',
                    margin: 2,
                    padding: '4px',
                    height: '70px',
                    width: '100%',
                }} 
            >
                <Tab 
                    label="Overview" 
                    icon={<DashboardIcon fontSize="small" />} 
                    iconPosition="start" 
                />
                <Tab 
                    label="Users" 
                    icon={<PeopleIcon fontSize="small" />} 
                    iconPosition="start" 
                />
                <Tab 
                    label="Progress"
                    icon={<TimelineIcon fontSize="small" />} 
                    iconPosition="start" 
                />
                <Tab 
                    label="Certifications" 
                    icon={<VerifiedIcon fontSize="small" />} 
                    iconPosition="start" 
                />
                <Tab 
                    label="Documents" 
                    icon={<DescriptionIcon fontSize="small" />} 
                    iconPosition="start" 
                />
            </Tabs>

            {tab.name === "Overview" && (
               <Overview 
                    program={currentProgram} 
                    updateDescription={(programId, description) => {
                        if (currentProgram != null && currentProgram.id == programId) {
                            const updatedProgram: Program = { ...currentProgram, description: description };
                            handleSaveProgram(updatedProgram)
                        } else {
                            console.error("could not save description, program invalid or id mismatch")
                        }
                    }}
                    updateNavigation={(programId, navigation) => {
                        if (currentProgram != null && currentProgram.id == programId) {
                            const updatedProgram: Program = { ...currentProgram, navigation: navigation };
                            handleSaveProgram(updatedProgram)
                        }
                    }}
                />
            )}
            
            {tab.name === "Users" && (
               <UserView/>
            )}

            {tab.name === "Progress" && (
                <UserProgressView/>
            )}

            {tab.name === "Certifications" && (
                <UserCertificationView />
            )}
    
            {tab.name === "Documents" && (
                <DocumentsUploadView 
                    updateSystemPrompt={(programId, systemPrompt) => {
                        if (currentProgram != null && currentProgram.id == programId) {
                            const updatedProgram: Program = { ...currentProgram, systemPrompt: systemPrompt };
                            handleSaveProgram(updatedProgram)
                        } else {
                            console.error("could not save system prompt, program invalid or id mismatch")
                        }
                    }}
                />
            )}
        </>
    )
}